import React from 'react';
import LabelValue from 'utils/components/LabelValue/LabelValue';
import { WidgetInfo } from 'widgets/WidgetInfo';
import DetailPanel from 'widgets/supportVisualisation/Content/DetailPanel';
import {
    selectLayoutComponents,
    useLayoutComponents,
} from 'layout/store/selectors';
import { updatingFromLayout } from 'layout/store/thunks';
import { useDispatch } from 'store/hooks';
import { createUpdateWidgetComponentStateAction } from 'widgets/store/component/actions';
import classNames from 'classnames';
import { WidgetType } from 'widgets/WidgetType';
import { addFillsGraphPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelFillsGraph';
import { emptySelectionState } from 'portfolio/PortfolioSelection';
import { addIntradayProfilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelIntradayProfiles';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { addVenueAnalysisPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelVenueAnalysis';
import { createSetStreamDataAction } from 'widgets/store/stream/actions';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import './WidgetPanel.scss';
import { addPortfolioReturnsPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelPortfolioReturns';
import { addPortfolioPlPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelPortfolioPl';
import {
    AppSettingsState,
    SymbologyValues,
} from 'app/store/appSettings/AppSettingsState';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { createLoadWidgetConfigurationAction } from 'widgets/store/configuration/actions';
import { addSymbolTilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelSymbolTile';
import { Tab } from '@instinet-analytics/semantic-ui-components';
import { addSignalTilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelSignalTile';
import { addCandlestickTilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelCandlestickTile';
import { addObvTilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelObvTile';
import { addPortfolioSummaryPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelPortfolioSummary';
import { addMyPortfolioCapsulesPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelMyPortfolioCapsules';
import { addEstimatedTradeCostPanes } from './WidgetPanelEstimatedTradeCost';
import { initialRsiTileConfigState } from 'widgets/signalTiles/rsiTile/state';
import { initialPfeTileConfigState } from 'widgets/signalTiles/pfeTile/state';
import { addVolumeTilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelVolumeCapsule';
import { addLiquiditySummaryPanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelLiquiditySummary';
import { WidgetPanelCorporateActions } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelCorporateActions';
import { selectUsersSymbology } from 'app/store/appSettings/selectors';
import { addHistoricalPercentagePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelHistoricalPercentageChange.tsx';
import { addVolumeCurvePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelVolumeCurve.tsx';
import { addOrderVolumeProfilePanes } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelOrderVolumeProfile.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyWidgetState = any;

export interface WidgetPanelProps {
    widget: WidgetInfo;
    state: ContactSupportRequestState;
    height: number;
    width: number;
}

export interface WidgetPanelDetailProps extends WidgetPanelProps {
    widgetState: AnyWidgetState;
    selection: PortfolioSelectionWrapper;
    appSettings: AppSettingsState;
    exchangeRate: number;
}

export default function WidgetPanel(props: WidgetPanelProps) {
    const { widget, height, width, state } = props;

    const widgetState = getWidgetState(state, widget);
    const selection = getSelection();
    const detailHeight = height - 35;

    const appSettings = state?.state?.app?.appSettings;
    const currency = appSettings?.currencyCode
        ? (state?.state?.app?.currencies ?? {})[appSettings.currencyCode]
        : undefined;
    const exchangeRate = currency ? currency.rate : 1;

    const detailProps = {
        ...props,
        widgetState,
        selection,
        height: detailHeight,
        appSettings,
        exchangeRate,
    };

    const renderPayload = () => (
        <div className="payload">
            <JsonPanel data={widgetState} />
        </div>
    );

    const panes = [
        {
            menuItem: 'State',
            render: renderPayload,
        },
    ];

    if (widget.type === WidgetType.CANDLESTICK_TILE) {
        addCandlestickTilePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.CORPORATE_ACTIONS) {
        const render = () => <WidgetPanelCorporateActions {...detailProps} />;
        panes.push({
            menuItem: 'Requests',
            render,
        });
    }
    if (widget.type === WidgetType.MY_PORTFOLIO_CAPSULES) {
        addMyPortfolioCapsulesPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.EST_TRADE_COST_WIDGET) {
        addEstimatedTradeCostPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.OBV_TILE) {
        addObvTilePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.FILLS_GRAPH) {
        addFillsGraphPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.VOLUME_CAPSULE) {
        addVolumeTilePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.VOLUME_CURVE) {
        addVolumeCurvePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.ORDER_VOLUME_PROFILE) {
        addOrderVolumeProfilePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.LIQUIDITY_SUMMARY) {
        addLiquiditySummaryPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.INTRA_DAY_PROFILE) {
        addIntradayProfilePanes(detailProps, panes);
    }
    if (widget.type === WidgetType.VENUE_ANALYSIS) {
        addVenueAnalysisPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.PORTFOLIO_RETURNS) {
        addPortfolioReturnsPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.PORTFOLIO_SUMMARY) {
        addPortfolioSummaryPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.PORTFOLIO_PL) {
        addPortfolioPlPanes(detailProps, panes);
    }
    if (widget.type === WidgetType.SYMBOL_TILE) {
        addSymbolTilePanes(detailProps, panes);
    }
    if (
        widget.type === WidgetType.HistoricalPercentageChangeWidget ||
        widget.type === WidgetType.HistoricalPercentageChangeCapsule
    ) {
        addHistoricalPercentagePanes(detailProps, panes);
    }

    if (widget.type === WidgetType.RSI_TILE) {
        addSignalTilePanes(
            30,
            70,
            detailProps,
            initialRsiTileConfigState,
            panes
        );
    }

    if (widget.type === WidgetType.PFE_TILE) {
        addSignalTilePanes(
            -50,
            50,
            detailProps,
            initialPfeTileConfigState,
            panes
        );
    }

    return (
        <DetailPanel className="WidgetPanel" height={height} width={width}>
            <div className="info">
                <LabelValue label="Type" value={widget.type} />
                <LabelValue label="Key" value={widget.id} />
            </div>
            <CopyStatePanel widget={widget} widgetState={widgetState} />
            <Tab
                className="widget-panel-tab"
                panes={panes}
                menu={{ secondary: true, pointing: true, inverted: true }}
            />
        </DetailPanel>
    );

    function getSelection(): PortfolioSelectionWrapper {
        const selection = {
            ...emptySelectionState,
            ...state?.portfolio.selection,
        };
        return new PortfolioSelectionWrapper(selection);
    }
}

const typeMappings: { [widgetType: string]: Array<WidgetType> } = {
    [WidgetType.OPEN_SUBMISSIONS]: [WidgetType.OPEN_SUBMISSIONS_CAPSULE],
};
function CopyStatePanel(props: {
    widget: WidgetInfo;
    widgetState?: AnyWidgetState;
}) {
    const { widget, widgetState } = props;
    const components = useLayoutComponents();
    const compatibleTargets = (components ?? []).filter((c) => {
        return (
            c.type === widget.type ||
            (typeMappings[widget.type] ?? []).indexOf(c.type) > -1
        );
    });

    if (compatibleTargets.length === 0) {
        return <></>;
    }
    return (
        <div className="copy-panel">
            <div className="title">Copy State</div>
            <table>
                {compatibleTargets.map((target) => (
                    <CopyWidgetState
                        key={target.id}
                        widget={widget}
                        target={target}
                        widgetState={widgetState}
                    />
                ))}
            </table>
        </div>
    );
}

function CopyWidgetState(props: {
    key: string;
    widget: WidgetInfo;
    target: WidgetInfo;
    widgetState?: AnyWidgetState;
}) {
    const { target, widgetState, key } = props;

    const config = widgetState?.config;
    const component = widgetState?.component;
    const streamData = widgetState?.stream?.data;

    const dispatch = useDispatch();

    const widgetUpdateConfigAction = createLoadWidgetConfigurationAction(
        target.type
    );

    const loadConfig = () => {
        if (!config) {
            return;
        }
        dispatch(
            updatingFromLayout(widgetUpdateConfigAction(target.id, config))
        );
    };

    const loadComponent = () => {
        if (!component) {
            return;
        }
        dispatch(
            createUpdateWidgetComponentStateAction(target.type)(
                target.id,
                component
            )
        );
    };

    const loadStream = () => {
        if (!streamData) {
            return;
        }
        dispatch(createSetStreamDataAction(target.type)(target.id, streamData));
    };

    return (
        <tr key={key}>
            <td>{target.id}</td>
            <td
                className={classNames('link', !config && 'disabled')}
                onClick={loadConfig}
            >
                Config
            </td>
            <td
                className={classNames('link', !component && 'disabled')}
                onClick={loadComponent}
            >
                Component
            </td>
            <td
                className={classNames('link', !streamData && 'disabled')}
                onClick={loadStream}
            >
                Stream Data
            </td>
        </tr>
    );
}

export function showNiceDate<T>(o: T, k: keyof T & string) {
    const value = o[k];
    if (typeof value === 'number') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (o as any)[k + 'Display'] = new Date(value);
    }
}

export function showTime<T>(o: T, k: keyof T & string) {
    const value = o[k];
    if (typeof value === 'number') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (o as any)[k + 'Display'] = new Date(value * 60000);
    }
}

export function selectCapsuleSymbolBindingFromState(
    props: WidgetPanelDetailProps
):
    | {
          symbol: string;
          symbology: SymbologyValues;
          source: 'Config' | 'Portfolio Selection' | 'Capsule Container Config';
      }
    | undefined {
    const { widgetState, selection, state } = props;
    const usersSymbology = selectUsersSymbology(state?.state);

    const widgetInfos = state?.state
        ? selectLayoutComponents(state?.state)
        : [];

    const capsuleContainerInfos = widgetInfos.filter(
        (w) => w.type === WidgetType.TILES
    );

    for (const cc of capsuleContainerInfos) {
        const ccConfig = getWidgetState(state, cc).config;
        if (ccConfig?.symbol) {
            return {
                symbol: ccConfig!.symbol,
                symbology: usersSymbology,
                source: 'Capsule Container Config',
            };
        }
    }

    if (widgetState?.config?.symbol) {
        return {
            symbol: widgetState.config.symbol,
            symbology: usersSymbology,
            source: 'Config',
        };
    }

    if (selection.selectedSymbol) {
        return {
            symbol: selection.selectedSymbol,
            symbology: selection.symbology,
            source: 'Portfolio Selection',
        };
    }

    return undefined;
}

function getWidgetState(state: ContactSupportRequestState, widget: WidgetInfo) {
    if (!state?.state?.widgets || !widget) {
        return undefined;
    }

    const widgetTypeState = state.state.widgets.instances[
        widget.type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any;

    return widgetTypeState[widget.id];
}
